import type { RawDraftContentState } from 'draft-js';
import { toast } from 'react-toastify';
import { client } from '~/client';
import type { FormValues } from '~/components/automation/AutomationForm/AutomationForm';
import {
  deserializeNotes,
  serializeNotes,
  toInput,
} from '~/components/job-template/JobTemplateForm';
import { getFragmentData, graphql } from '~/gql';
import type {
  CreateAutomationMutation,
  CreateJobTemplateMutation,
  UpdateJobTemplateMutation,
} from '~/gql/graphql';
import {
  AutomationInput,
  AutomationRepeatInput,
  AutomationWhereType,
  JobTemplateFieldsFragment,
  JobTemplateInput,
  Status,
  TaskInput,
} from '~/gql/graphql';
import { JobTemplateFields } from '~/graphql/fragment/JobTemplateFields';
import { RepeatValue, repeatToInput } from '../components/ui/JobsRepeat';
import {
  toAttachmentInput,
  type Kind,
  type UploadResult,
} from '../components/upload2';
import type { JobConfig } from './templateHelpers';
import {
  findMatchingTemplate,
  isSystemGeneratedTemplate,
} from './templateHelpers';

const safelyDeserializeTemplateNotes = (
  notes: string | null | undefined,
  templateId?: string
) => {
  if (!notes) {
    return null;
  }

  try {
    return deserializeNotes(notes);
  } catch (error) {
    console.error('Failed to deserialize template notes', {
      error,
      templateId,
    });
    return null;
  }
};

const ListJobTemplatesDocument = graphql(`
  query ListJobTemplates($status: Status) {
    jobTemplates(status: $status) {
      id
      name
      ...JobTemplateFields
    }
  }
`);

// Type for urql mutation result
type MutationResult<T> = Promise<{
  data?: T;
  error?: { message: string };
}>;

/**
 * Result of template/automation creation process
 */
export type TemplateAutomationResult = {
  templateId: string | null;
  automationId: string | null;
  createdTemplate: boolean;
  createdAutomation: boolean;
};

/**
 * Error thrown during template/automation creation
 */
export class TemplateAutomationError extends Error {
  constructor(
    message: string,
    public readonly step: 'template' | 'automation' | 'unknown',
    public readonly originalError?: unknown
  ) {
    super(message);
    this.name = 'TemplateAutomationError';
  }
}

/**
 * Compares two arrays of strings for equality (order-sensitive)
 */
function arraysEqual(
  arr1: readonly string[],
  arr2: readonly string[]
): boolean {
  if (arr1.length !== arr2.length) return false;
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }
  return true;
}

/**
 * Compares two arrays of strings for equality (order-insensitive, using sorted comparison)
 */
function arraysEqualSorted(
  arr1: readonly string[],
  arr2: readonly string[]
): boolean {
  if (arr1.length !== arr2.length) return false;
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return arraysEqual(sorted1, sorted2);
}

/**
 * Compares task attachments/identifiers for equality
 * Compares by id, filename (originalFilename), and url/hash if available
 */
function attachmentsEqual(
  formAttachments: Array<{
    id: string;
    kind: string;
    ext: string;
    thumb: string;
    url: string;
    originalFilename: string;
  }>,
  originalAttachments: Array<{
    id: string;
    kind: string;
    ext: string;
    thumb: string;
    url: string;
    originalFilename: string;
  }>
): boolean {
  if (formAttachments.length !== originalAttachments.length) return false;

  // Create maps for efficient lookup
  const formMap = new Map(
    formAttachments.map((att) => [
      att.id,
      { filename: att.originalFilename, url: att.url },
    ])
  );
  const originalMap = new Map(
    originalAttachments.map((att) => [
      att.id,
      { filename: att.originalFilename, url: att.url },
    ])
  );

  // Check all form attachments exist in original with same filename/url
  for (const [id, formData] of formMap.entries()) {
    const originalData = originalMap.get(id);
    if (
      !originalData ||
      originalData.filename !== formData.filename ||
      originalData.url !== formData.url
    ) {
      return false;
    }
  }

  // Check all original attachments exist in form
  return formMap.size === originalMap.size;
}

type Tag = { id: string };

/**
 * Normalizes tag collections that may be either a flat array of tags or a
 * connection-style object with a `nodes` array, returning a simple array of IDs.
 */
export function getTagIds(
  tags: readonly Tag[] | { nodes?: readonly Tag[] | null } | null | undefined
): string[] {
  if (!tags) {
    return [];
  }

  if (Array.isArray(tags)) {
    return (tags as readonly Tag[]).map((tag: Tag) => tag.id);
  }

  const connection = tags as { nodes?: readonly Tag[] | null };
  return (connection.nodes ?? []).map((tag: Tag) => tag.id);
}

/**
 * Deep comparison of a single task
 */
function tasksEqual(
  formTask: TaskInput,
  originalTask: JobTemplateFieldsFragment['tasks'][number]
): boolean {
  // Compare basic fields
  if (formTask.id !== originalTask.id) return false;
  if (formTask.name !== originalTask.name) return false;
  if ((formTask.description || '') !== (originalTask.description || ''))
    return false;
  if (formTask.type !== originalTask.type) return false;

  // Compare required flag (only if present in formTask, as original template may not have it)
  // Note: originalTemplate tasks don't include required field in fragment, so we skip it

  // Compare attributes (site attributes) - array of IDs
  const formAttributes = (formTask.attributes || []).sort();
  const originalAttributes = (
    originalTask.attributes?.map((attr) => attr.id) || []
  ).sort();
  if (!arraysEqual(formAttributes, originalAttributes)) return false;

  // Compare jobAttributes - array of IDs
  const formJobAttributes = (formTask.jobAttributes || []).sort();
  const originalJobAttributes = (originalTask.jobAttributeIds || []).sort();
  if (!arraysEqual(formJobAttributes, originalJobAttributes)) return false;

  // Compare nested task-specific fields by normalizing to stable strings

  // Helper to create stable string representation of nested objects
  const normalizeObject = (obj: unknown): string => {
    if (obj == null) return '';
    if (
      typeof obj === 'string' ||
      typeof obj === 'number' ||
      typeof obj === 'boolean'
    ) {
      return String(obj);
    }
    if (Array.isArray(obj)) {
      return obj.map(normalizeObject).sort().join(',');
    }
    if (typeof obj === 'object' && obj !== null) {
      // Sort object keys and recursively normalize values
      return Object.keys(obj)
        .sort()
        .map(
          (key) =>
            `${key}:${normalizeObject((obj as Record<string, unknown>)[key])}`
        )
        .join('|');
    }
    return '';
  };

  // Compare asset-related configuration
  if (formTask.type === 'Asset') {
    // Form side: configuration is stored on `asset`
    const formAsset = (
      formTask as TaskInput & {
        asset?: {
          selection?: string;
          assets?: unknown[];
          assetTypes?: unknown[];
          allow?: unknown[];
          spaces?: unknown[];
        };
      }
    ).asset;

    const formAssetIds: string[] = Array.isArray(formAsset?.assets)
      ? formAsset.assets.map((asset) =>
          typeof asset === 'string'
            ? asset
            : (asset as { id?: string }).id ?? ''
        )
      : [];

    // Original side: selection/filters live on `config`, specific assets on `assets`
    const originalConfig = (
      originalTask as unknown as {
        config?: {
          selection?: string;
          assetTypes?: unknown[];
          allow?: unknown[];
          spaces?: unknown[];
        };
      }
    ).config;

    const originalAssetIds: string[] = Array.isArray(originalTask.assets)
      ? (originalTask.assets as { id: string }[]).map((asset) => asset.id)
      : [];

    // Normalize selection: if original doesn't have selection, default to 'specific'
    // (matching taskTemplateToTaskInput behavior which defaults to 'specific')
    const formSelection = formAsset?.selection ?? 'specific';
    const originalSelection = originalConfig?.selection ?? 'specific';

    const formAssetSignature = normalizeObject({
      selection: formSelection,
      assets: formAssetIds,
      assetTypes: Array.isArray(formAsset?.assetTypes)
        ? formAsset?.assetTypes
        : [],
      allow: Array.isArray(formAsset?.allow) ? formAsset?.allow : [],
      spaces: Array.isArray(formAsset?.spaces) ? formAsset?.spaces : [],
    });

    const originalAssetSignature = normalizeObject({
      selection: originalSelection,
      assets: originalAssetIds,
      assetTypes: Array.isArray(originalConfig?.assetTypes)
        ? originalConfig?.assetTypes
        : [],
      allow: Array.isArray(originalConfig?.allow) ? originalConfig?.allow : [],
      spaces: Array.isArray(originalConfig?.spaces)
        ? originalConfig?.spaces
        : [],
    });

    if (formAssetSignature !== originalAssetSignature) return false;
  } else {
    // Non-asset tasks: keep existing generic comparisons
    const formAsset = normalizeObject(formTask.asset);
    const originalAssets = normalizeObject(originalTask.assets);
    if (formAsset !== originalAssets) return false;
  }

  // Compare attribute field
  if (formTask.type === 'Attribute') {
    // For Attribute tasks we only care about the attribute IDs themselves,
    // not the wrapper object, id, or __typename. The formTask stores
    // configuration as `attribute: { attributes: string[] }` while the
    // original template includes an id and __typename as well.
    const formAttributeIds: string[] = Array.isArray(
      (formTask as unknown as { attribute?: { attributes?: string[] } })
        .attribute?.attributes
    )
      ? (
          (formTask as unknown as { attribute?: { attributes?: string[] } })
            .attribute?.attributes ?? []
        ).slice()
      : [];

    const originalAttributeIds: string[] = Array.isArray(
      (
        originalTask as unknown as {
          attribute?: { attributes?: string[] | null };
        }
      ).attribute?.attributes
    )
      ? (
          (
            originalTask as unknown as {
              attribute?: { attributes?: string[] | null };
            }
          ).attribute?.attributes ?? []
        ).slice()
      : [];

    if (!arraysEqualSorted(formAttributeIds, originalAttributeIds)) {
      return false;
    }
  } else {
    const formAttribute = normalizeObject(formTask.attribute);
    const originalAttribute = normalizeObject(originalTask.attribute);
    if (formAttribute !== originalAttribute) return false;
  }

  // Compare purchase field
  const formPurchase = normalizeObject(formTask.purchase);
  const originalPurchase = normalizeObject(originalTask.purchase);
  if (formPurchase !== originalPurchase) return false;

  // Compare stocktake field
  const formStocktake = normalizeObject(formTask.stocktake);
  const originalStocktake = normalizeObject(originalTask.stocktake);
  if (formStocktake !== originalStocktake) return false;

  // Compare config field (non-asset tasks only – asset config is compared via asset signature above)
  if (formTask.type !== 'Asset') {
    const formConfig = normalizeObject(
      (formTask as unknown as { config?: unknown }).config
    );
    const originalConfig = normalizeObject(
      (originalTask as unknown as { config?: unknown }).config
    );
    if (formConfig !== originalConfig) return false;
  }

  // Compare task-level tags (by ID)
  const formTagIds = (formTask.tagIds || []).sort();
  const originalTagIds = getTagIds(originalTask.tags).sort();
  if (!arraysEqual(formTagIds, originalTagIds)) return false;

  return true;
}

/**
 * Detects whether any Asset task with selection='specific' has had its
 * asset selection changed compared to the original template.
 *
 * This is used to decide when we should *only* allow creating a new template
 * instead of updating the existing one (to avoid mutating pinned asset setups).
 */
export function hasModifiedSpecificAssetTask(
  formData: FormValues,
  originalTemplate: JobTemplateFieldsFragment
): boolean {
  if (!formData.templateData) {
    return false;
  }

  const formTasks = formData.templateData.tasks.filter(
    (task) => !task._destroy
  );

  if (!formTasks.length) {
    return false;
  }

  const originalTasksById = new Map<
    string,
    JobTemplateFieldsFragment['tasks'][number]
  >((originalTemplate.tasks || []).map((task) => [task.id, task]));

  for (const formTask of formTasks) {
    if (formTask.type !== 'Asset') {
      continue;
    }

    const selection = formTask.asset?.selection ?? null;
    if (selection !== 'specific') {
      continue;
    }

    const formAssetIds: string[] = Array.isArray(formTask.asset?.assets)
      ? (formTask.asset?.assets as unknown[]).map((asset) =>
          typeof asset === 'string'
            ? asset
            : (asset as { id?: string }).id ?? ''
        )
      : [];

    const originalTask = originalTasksById.get(formTask.id);

    // New specific-asset task that didn't exist on the original template
    if (!originalTask) {
      if (formAssetIds.length > 0) {
        return true;
      }
      continue;
    }

    const originalAssetIds: string[] = Array.isArray(originalTask.assets)
      ? (originalTask.assets as { id: string }[]).map((asset) => asset.id)
      : [];

    if (!arraysEqualSorted(formAssetIds, originalAssetIds)) {
      return true;
    }
  }

  return false;
}

function normalizeNotesForComparison(
  notes: RawDraftContentState | string | null | undefined
): string {
  if (!notes) return '';

  const parsedNotes =
    typeof notes === 'string'
      ? safelyDeserializeTemplateNotes(notes)
      : (notes as RawDraftContentState | null | undefined);

  if (!parsedNotes) {
    return '';
  }

  return serializeNotes(parsedNotes) ?? '';
}

/**
 * Checks if template data in form has been modified compared to the original template
 *
 * @param formData - Form values containing templateData
 * @param originalTemplate - Original template to compare against
 * @returns true if template was modified, false otherwise
 */
export function isTemplateModified(
  formData: FormValues,
  originalTemplate: JobTemplateFieldsFragment
): boolean {
  if (!formData.templateData || !originalTemplate) return false;

  // Compare name
  if (formData.templateData.name !== originalTemplate.name) {
    return true;
  }

  // Compare notes
  const formNotes = normalizeNotesForComparison(formData.templateData.notes);
  const originalNotes = normalizeNotesForComparison(originalTemplate.notes);
  if (formNotes !== originalNotes) {
    return true;
  }

  // Compare tags
  const formTags = (formData.templateData.tags || []).sort();
  const originalTags = getTagIds(originalTemplate.tags).sort();
  if (!arraysEqual(formTags, originalTags)) {
    return true;
  }

  // Compare tasks - deep comparison
  const formTasks = formData.templateData.tasks.filter(
    (task) => !task._destroy
  ); // Exclude tasks marked for deletion
  const originalTasks = originalTemplate.tasks || [];

  // Compare task counts
  if (formTasks.length !== originalTasks.length) {
    return true;
  }

  // Detect reordering by comparing task id sequences
  const formTaskIds = formTasks.map((task) => task.id);
  const originalTaskIds = originalTasks.map((task) => task.id);
  if (!arraysEqual(formTaskIds, originalTaskIds)) {
    return true;
  }

  // Deep compare each task by index (respecting order)
  for (let i = 0; i < formTasks.length; i++) {
    if (!tasksEqual(formTasks[i], originalTasks[i])) {
      return true;
    }
  }

  // Compare template-level attachments
  const formAttachments = formData.templateData.attachments || [];
  const originalAttachments = originalTemplate.attachments || [];
  if (!attachmentsEqual(formAttachments, originalAttachments)) {
    return true;
  }

  // Compare includeAttributes - treat undefined as baseline (field not in fragment yet)
  // Only compare when originalTemplate has a defined value (null or array)
  if (originalTemplate.includeAttributes !== undefined) {
    // Normalize null and empty array as equivalent
    const formAttrs = formData.templateData.includeAttributes ?? null;
    const originalAttrs = originalTemplate.includeAttributes ?? null;

    const formNormalized = formAttrs === null ? [] : formAttrs;
    const originalNormalized = originalAttrs === null ? [] : originalAttrs;

    if (!arraysEqualSorted(formNormalized, originalNormalized)) {
      return true;
    }
  }

  // Compare jobAttributeIds - treat undefined as baseline (field not in fragment yet)
  // Only compare when originalTemplate has a defined value (null or array)
  if (originalTemplate.jobAttributeIds !== undefined) {
    // Normalize null and empty array as equivalent
    const formJobAttrs = formData.templateData.jobAttributeIds ?? null;
    const originalJobAttrs = originalTemplate.jobAttributeIds ?? null;

    const formNormalized = formJobAttrs === null ? [] : formJobAttrs;
    const originalNormalized =
      originalJobAttrs === null ? [] : originalJobAttrs;

    if (!arraysEqualSorted(formNormalized, originalNormalized)) {
      return true;
    }
  }

  return false;
}

/**
 * Result of template action (update/create)
 */
export type TemplateActionResult = {
  success: boolean;
  formData: FormValues;
  error?: string;
};

/**
 * Handles template update or create action based on user choice
 *
 * @param params - Parameters for template action
 * @returns Result object with success status and updated form data
 */
export async function handleTemplateAction(params: {
  action: 'update' | 'create';
  formData: FormValues;
  jobTemplates: JobTemplateFieldsFragment[];
  updateJobTemplate: (input: {
    id: string;
    input: JobTemplateInput;
  }) => MutationResult<UpdateJobTemplateMutation>;
  createJobTemplate: (input: {
    input: JobTemplateInput;
  }) => MutationResult<CreateJobTemplateMutation>;
}): Promise<TemplateActionResult> {
  const {
    action,
    formData,
    jobTemplates,
    updateJobTemplate,
    createJobTemplate,
  } = params;

  if (!formData.templateData || !formData.template) {
    return {
      success: false,
      formData,
      error: 'Template data or template ID is missing',
    };
  }

  try {
    const selectedTemplate = jobTemplates.find(
      (t) => t.id === formData.template
    );

    if (!selectedTemplate) {
      return {
        success: false,
        formData,
        error: 'Selected template not found',
      };
    }

    const wasSystemTemplate = isSystemGeneratedTemplate(selectedTemplate);
    let finalName = formData.templateData.name.trim();

    if (action === 'update') {
      // When updating, preserve "[System]" suffix for system templates
      if (wasSystemTemplate) {
        const originalName = selectedTemplate.name;
        const wasSystemRemoved =
          originalName.endsWith(' [System]') &&
          !finalName.endsWith(' [System]');

        finalName = wasSystemRemoved
          ? finalName
          : finalName.endsWith(' [System]')
          ? finalName
          : `${finalName} [System]`;
      } else {
        // User templates should never have "[System]" suffix
        finalName = finalName.replace(/\s*\[System\]\s*$/, '');
      }
    } else {
      // When creating new, remove "[System]" suffix (new template is user template)
      finalName = finalName.replace(/\s*\[System\]\s*$/, '').trim();
      // Suggest " (Copy)" if not already present
      if (!finalName.includes(' (Copy)')) {
        finalName = `${finalName} (Copy)`;
      }
    }

    // Convert template data attachments to UploadResult format (toInput expects UploadResult[])
    const attachments: UploadResult[] = formData.templateData.attachments.map(
      (att) => ({
        id: att.id,
        kind: att.kind as Kind,
        name: att.originalFilename,
        preview: att.url || '',
      })
    );

    // Normalise asset tasks before converting to JobTemplateInput to satisfy
    // backend validation rules and avoid sending editor-only fields.
    const normalisedTasks: TaskInput[] = formData.templateData.tasks.map(
      (task) => {
        if (task.type !== 'Asset') {
          return task;
        }

        const asset = task.asset;

        // Strip editor-only fields that are not relevant for Asset tasks
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- intentionally omitting non-Asset fields
        const { attribute, purchase, stocktake, ...rest } = task as TaskInput;

        if (!asset) {
          return {
            ...rest,
            asset: undefined,
          };
        }

        const selection = asset.selection ?? 'specific';
        const assets = Array.isArray(asset.assets) ? asset.assets : [];
        const assetTypes = Array.isArray(asset.assetTypes)
          ? asset.assetTypes
          : [];
        const spaces = Array.isArray(asset.spaces) ? asset.spaces : [];
        const allow = Array.isArray(asset.allow) ? asset.allow : [];

        // Sharing permissions for external assignees (with defaults)
        const sharingFields = {
          shareAttributes: asset.shareAttributes ?? true,
          shareJobHistory: asset.shareJobHistory ?? true,
          shareDeploymentHistory: asset.shareDeploymentHistory ?? false,
        };

        let normalisedAsset: TaskInput['asset'];

        if (selection === 'specific') {
          if (assets.length === 0) {
            // Backend is strict about "selection: specific" with no assets.
            // If the user hasn't chosen any specific assets, treat this as an
            // "all assets" task so the template update can succeed.
            normalisedAsset = {
              selection: 'all',
              assetTypes,
              spaces,
              allow,
              ...sharingFields,
            };
          } else {
            normalisedAsset = {
              selection: 'specific',
              assets,
              allow,
              ...sharingFields,
            };
          }
        } else {
          // "all" or other non-specific selections never send an assets list.
          normalisedAsset = {
            selection,
            assetTypes,
            spaces,
            allow,
            ...sharingFields,
          };
        }

        return {
          ...rest,
          asset: normalisedAsset,
        };
      }
    );

    // Convert template data to JobTemplateInput
    const templateInput = toInput(normalisedTasks, {
      name: finalName,
      notes: formData.templateData.notes || null,
      tags: formData.templateData.tags.length
        ? formData.templateData.tags
        : null,
      includeAttributes: formData.templateData.includeAttributes,
      jobAttributes: formData.templateData.jobAttributeIds,
      attachments,
      status: Status.Active,
    });

    let newTemplateId = formData.template;

    if (action === 'create') {
      // Create new template
      const createResult = await createJobTemplate({
        input: templateInput,
      });

      if (createResult.error) {
        const errorMessage =
          'Failed to create template: ' +
          (createResult.error.message || 'Unknown error').replace(
            '[GraphQL] ',
            ''
          );
        toast.error(errorMessage);
        return {
          success: false,
          formData,
          error: errorMessage,
        };
      }

      if (!createResult.data?.createJobTemplate?.id) {
        const errorMessage = 'Failed to create template. Please try again.';
        toast.error(errorMessage);
        return {
          success: false,
          formData,
          error: errorMessage,
        };
      }

      newTemplateId = createResult.data.createJobTemplate.id;
      toast.success('New template created successfully');
    } else {
      // Update existing template
      const updateResult = await updateJobTemplate({
        id: formData.template,
        input: templateInput,
      });

      if (updateResult.error) {
        const errorMessage =
          'Failed to update template: ' +
          (updateResult.error.message || 'Unknown error').replace(
            '[GraphQL] ',
            ''
          );
        toast.error(errorMessage);
        return {
          success: false,
          formData,
          error: errorMessage,
        };
      }

      if (!updateResult.data?.updateJobTemplate?.id) {
        const errorMessage = 'Failed to update template. Please try again.';
        toast.error(errorMessage);
        return {
          success: false,
          formData,
          error: errorMessage,
        };
      }

      toast.success('Template updated successfully');
    }

    // Update form data to use the template (new or updated)
    return {
      success: true,
      formData: {
        ...formData,
        template: newTemplateId,
      },
    };
  } catch (error) {
    console.error('Unexpected error during template operation:', error);
    const errorMessage =
      'An unexpected error occurred while processing template. Please try again.';
    toast.error(errorMessage);
    return {
      success: false,
      formData,
      error: errorMessage,
    };
  }
}

/**
 * Ensures a template exists for automation creation.
 * Checks if selectedTemplate can be reused, searches for matching template,
 * or creates a new system template if needed.
 *
 * NOTE: This function automatically handles duplicate template names by generating
 * numbered variants (e.g., "Job Name [System] (2)"). This is ONLY for system-generated
 * templates created automatically during repeat automation creation. Manual template
 * creation (via handleTemplateAction or CreateTemplateButton) does NOT use this logic
 * and will show errors to users if duplicate names are detected.
 *
 * @param jobConfig - Job configuration for template matching
 * @param processedTasks - Processed task inputs for template creation
 * @param selectedTemplate - Currently selected template (if any)
 * @param isTemplateModified - Function to check if template was modified
 * @param createJobTemplate - Mutation function to create templates
 * @param formData - Form data for template creation
 * @returns Object with templateId and whether a new template was created
 */
async function ensureTemplateForAutomation(
  jobConfig: JobConfig,
  processedTasks: TaskInput[],
  selectedTemplate: JobTemplateFieldsFragment | null,
  isTemplateModified: (template: JobTemplateFieldsFragment) => boolean,
  createJobTemplate: (input: {
    input: JobTemplateInput;
  }) => MutationResult<CreateJobTemplateMutation>,
  formData: {
    name: string;
    notes?: RawDraftContentState | null;
    tags?: string[] | null;
    includeAttributes?: string[] | null;
    jobAttributes?: string[] | null;
    attachments?: Array<{
      id: string;
      kind: Kind;
      name?: string;
      preview: string;
    }>;
    guestyCleaningStatus?: boolean;
  }
): Promise<{ templateId: string | null; createdTemplate: boolean }> {
  // If selected template exists and is not modified, reuse it
  if (selectedTemplate && !isTemplateModified(selectedTemplate)) {
    return { templateId: selectedTemplate.id, createdTemplate: false };
  }

  // Query all templates to find a match
  const { data: templatesData, error: templatesError } = await client
    .query(ListJobTemplatesDocument, { status: undefined })
    .toPromise();

  if (templatesError) {
    throw new TemplateAutomationError(
      'Failed to fetch templates. Please try again.',
      'template',
      templatesError
    );
  }

  const allTemplates = getFragmentData(
    JobTemplateFields,
    templatesData?.jobTemplates || []
  ) as JobTemplateFieldsFragment[];

  // Find matching template
  const matchingTemplate = findMatchingTemplate(jobConfig, allTemplates);

  if (matchingTemplate) {
    return { templateId: matchingTemplate.id, createdTemplate: false };
  }

  // Helper function to check if error indicates duplicate name
  const isDuplicateNameError = (errorMessage: string): boolean => {
    const lowerMessage = errorMessage.toLowerCase();
    return (
      lowerMessage.includes('already exists') ||
      lowerMessage.includes('duplicate') ||
      (lowerMessage.includes('name') &&
        (lowerMessage.includes('taken') ||
          lowerMessage.includes('exists') ||
          lowerMessage.includes('unique'))) ||
      lowerMessage.includes('name is taken')
    );
  };

  // Helper function to generate unique template name
  const generateUniqueTemplateName = (
    baseName: string,
    existingNames: string[]
  ): string => {
    const baseTemplateName = `${baseName} [System]`;
    const existingLower = existingNames.map((n) => n.toLowerCase().trim());

    // Check if base name is available
    if (!existingLower.includes(baseTemplateName.toLowerCase().trim())) {
      return baseTemplateName;
    }

    // Try numbered variants: "Name [System] (2)", "Name [System] (3)", etc.
    let counter = 2;
    let candidateName = `${baseName} [System] (${counter})`;
    while (existingLower.includes(candidateName.toLowerCase().trim())) {
      counter++;
      candidateName = `${baseName} [System] (${counter})`;
      // Safety limit to prevent infinite loop
      if (counter > 1000) {
        // Fallback to timestamp-based name
        candidateName = `${baseName} [System] (${Date.now()})`;
        break;
      }
    }

    return candidateName;
  };

  // Helper function to build template input
  const buildTemplateInput = (name: string): JobTemplateInput => ({
    name,
    tasks: processedTasks.map((task) => {
      // Some UIs store additional configuration on `task.config` during editing.
      // GraphQL TaskInput does not have a `config` field, so we must merge
      // config into the appropriate nested input (asset/attribute/item/transfer/etc).
      const config: Record<string, unknown> | undefined = (
        task as unknown as { config?: Record<string, unknown> }
      ).config;

      // Utility: shallow omit __typename from config if present
      const omitTypename = <T extends Record<string, unknown> | undefined>(
        obj: T
      ): T => {
        if (!obj) return obj;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { __typename, ...rest } = obj as Record<string, unknown>;
        return rest as T;
      };

      // Start with common/base fields
      const result: TaskInput = {
        id: task.id,
        type: task.type,
        name: task.name,
        description: task.description,
        attributes: task.attributes,
        jobAttributes: task.jobAttributes,
      };

      // Preserve existing nested fields if already provided on the task
      if (task.attribute) result.attribute = task.attribute;
      if (task.asset) result.asset = task.asset;
      if (task.purchase) result.purchase = task.purchase;
      if (task.stocktake) result.stocktake = task.stocktake;
      if ((task as TaskInput).item) result.item = (task as TaskInput).item;
      if ((task as TaskInput).transfer)
        result.transfer = (task as TaskInput).transfer;
      if ((task as TaskInput).required != null)
        result.required = (task as TaskInput).required;
      if ((task as TaskInput).tagIds)
        result.tagIds = (task as TaskInput).tagIds;
      if ((task as TaskInput)._destroy != null)
        (result as TaskInput)._destroy = (task as TaskInput)._destroy;

      // Merge config into corresponding nested inputs by task type
      switch (task.type) {
        case 'Asset': {
          const cleaned = omitTypename(config);
          // AssetTaskInput allowed: selection, allow, assetTypes, assets, deploy, spaces
          const selection =
            (result.asset as { selection?: string } | undefined)?.selection ||
            (cleaned?.selection as string | undefined) ||
            'specific';
          result.asset = {
            selection,
            ...(cleaned as Record<string, unknown> | undefined),
            ...(result.asset as Record<string, unknown> | undefined),
          } as TaskInput['asset'];
          break;
        }
        case 'Attribute': {
          const cleaned = omitTypename(config);
          // AttributeTaskInput: attributes: string[]
          const attributesFromConfig =
            (cleaned?.attributes as string[] | undefined) || [];
          const attributesFromTask =
            result.attribute &&
            (result.attribute as { attributes?: string[] }).attributes
              ? (result.attribute as { attributes?: string[] }).attributes
              : undefined;
          result.attribute = {
            attributes: attributesFromTask || attributesFromConfig || [],
          };
          break;
        }
        case 'Item': {
          const cleaned = omitTypename(config);
          // ItemTaskInput: dispatch, fillTo, locationId, processQueuedChanges, receive, reserveStock, selectStock, supplyJob
          result.item = {
            ...(cleaned as Record<string, unknown> | undefined),
            ...(result.item as Record<string, unknown> | undefined),
          } as TaskInput['item'];
          break;
        }
        case 'Purchase': {
          const cleaned = omitTypename(config);
          // PurchaseTaskInput: spaces?: string[]
          result.purchase = {
            ...(cleaned as Record<string, unknown> | undefined),
            ...(result.purchase as Record<string, unknown> | undefined),
          } as TaskInput['purchase'];
          break;
        }
        case 'Stocktake': {
          const cleaned = omitTypename(config);
          // StocktakeTaskInput: include?: ItemType[], itemStatus?: Status[], spaces?: string[]
          result.stocktake = {
            ...(cleaned as Record<string, unknown> | undefined),
            ...(result.stocktake as Record<string, unknown> | undefined),
          } as TaskInput['stocktake'];
          break;
        }
        case 'Transfer': {
          const cleaned = omitTypename(config);
          // TransferTaskInput: from, to, include
          result.transfer = {
            ...(cleaned as Record<string, unknown> | undefined),
            ...(result.transfer as Record<string, unknown> | undefined),
          } as TaskInput['transfer'];
          break;
        }
        default: {
          // For unknown/custom types, no-op; base fields already included
          break;
        }
      }

      return result;
    }),
    notes: serializeNotes(formData.notes) ?? null,
    tags: formData.tags || null,
    status: Status.Active,
    includeAttributes: formData.includeAttributes ?? null,
    jobAttributes: formData.jobAttributes ?? null,
    attachments: (formData.attachments || [])
      .filter(
        (
          att
        ): att is { id: string; kind: Kind; name?: string; preview: string } =>
          !!att.preview
      )
      .map(toAttachmentInput),
    guestyCleaningStatus: formData.guestyCleaningStatus ? 'clean' : null,
  });

  // Get existing template names for duplicate checking
  const existingTemplateNames = allTemplates.map((t) => t.name);

  // Create new template with "[System]" suffix, retry with numbered variant if duplicate
  let templateName = generateUniqueTemplateName(
    formData.name,
    existingTemplateNames
  );
  let templateInput = buildTemplateInput(templateName);
  let templateResult = await createJobTemplate({
    input: templateInput,
  });

  // If we get a duplicate name error, try with a numbered variant
  if (
    templateResult.error &&
    isDuplicateNameError(templateResult.error.message || '')
  ) {
    // Refresh template list to get latest names (in case another template was created)
    const { data: refreshedTemplatesData } = await client
      .query(ListJobTemplatesDocument, { status: undefined })
      .toPromise();

    const refreshedTemplates = getFragmentData(
      JobTemplateFields,
      refreshedTemplatesData?.jobTemplates || []
    ) as JobTemplateFieldsFragment[];

    const refreshedNames = refreshedTemplates.map((t) => t.name);
    templateName = generateUniqueTemplateName(formData.name, refreshedNames);
    templateInput = buildTemplateInput(templateName);
    templateResult = await createJobTemplate({
      input: templateInput,
    });
  }

  if (templateResult.error) {
    throw new TemplateAutomationError(
      `Failed to create template: ${(
        templateResult.error.message || 'Unknown error'
      ).replace('[GraphQL] ', '')}`,
      'template',
      templateResult.error
    );
  }

  if (!templateResult.data?.createJobTemplate?.id) {
    throw new TemplateAutomationError(
      'Failed to create template. Please try again.',
      'template'
    );
  }

  return {
    templateId: templateResult.data.createJobTemplate.id,
    createdTemplate: true,
  };
}

/**
 * Creates a repeat automation for maintaining job repeats.
 *
 * @param templateId - Template ID to use for automation
 * @param automationData - Automation configuration
 * @param createAutomation - Mutation function to create automations
 * @returns Automation ID
 */
async function createRepeatAutomation(
  templateId: string,
  automationData: {
    name: string;
    numOfRepeats: string;
    scopeIds: string[];
    owner?: string[] | null;
    assignee?: string[] | null;
    scheduledStartTime?: string | null;
    scheduledStartDate?: string | null;
    timeZone: string;
    repeatValue: RepeatValue;
  },
  createAutomation: (input: {
    input: AutomationInput;
  }) => MutationResult<CreateAutomationMutation>
): Promise<string> {
  let automationRepeat: AutomationRepeatInput | undefined;
  let repeatCount: number | undefined;

  if (automationData.repeatValue?.isRepeat) {
    const repeatInput = repeatToInput(automationData.repeatValue);
    if (repeatInput) {
      repeatCount =
        typeof repeatInput.count === 'number' &&
        !Number.isNaN(repeatInput.count)
          ? repeatInput.count
          : undefined;
      automationRepeat = {
        freq: repeatInput.freq,
        interval: repeatInput.interval,
        byday:
          repeatInput.byday && repeatInput.byday.length > 0
            ? repeatInput.byday
            : undefined,
        bymonthday: repeatInput.bymonthday || undefined,
      };
    }
  }

  const parsedRepeatTotal = (() => {
    if (repeatCount && repeatCount > 0) {
      return repeatCount;
    }
    const parsed = parseInt(automationData.numOfRepeats, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
  })();

  // Send local date/time as-is (no UTC conversion) for consistency with job creation
  const repeatStartDate =
    automationData.scheduledStartDate && automationData.scheduledStartTime
      ? `${automationData.scheduledStartDate}T${automationData.scheduledStartTime}:00`
      : automationData.scheduledStartDate || null;

  const automationInput: AutomationInput = {
    name: `${automationData.name}`,
    status: Status.Active,
    on: 'repeat',
    whereType: AutomationWhereType.Select,
    matchAttribute: [],
    sites: automationData.scopeIds,
    jobName: automationData.name,
    template: templateId,
    owner: automationData.owner || null,
    assignee: automationData.assignee || null,
    day: 0, // Same day
    time: automationData.scheduledStartTime || null,
    repeatStartDate,
    repeat: automationRepeat,
    runOnExistingBookings: false,
    maintainRepeats: parsedRepeatTotal ?? 1,
  };

  const automationResult = await createAutomation({
    input: automationInput,
  });

  if (automationResult.error) {
    throw new TemplateAutomationError(
      `Failed to create automation: ${(
        automationResult.error.message || 'Unknown error'
      ).replace('[GraphQL] ', '')}`,
      'automation',
      automationResult.error
    );
  }

  if (!automationResult.data?.createAutomation) {
    throw new TemplateAutomationError(
      'Failed to create automation. Please try again.',
      'automation'
    );
  }

  return automationResult.data.createAutomation;
}

/**
 * Creates template and automation for repeat job automation.
 * This function orchestrates the entire flow:
 * 1. Ensures a template exists (reuse, match, or create)
 * 2. Creates automation using the template
 *
 * NOTE: When an automation with `on: 'repeat'` is created, the backend immediately fires
 * `fireRepeatAutomationEvent`, which creates jobs based on the automation's schedule.
 * If job creation fails after this succeeds, the automation will persist and will have
 * already created (or will create) jobs according to its schedule. Duplicate detection
 * prevents duplicate jobs, but unwanted jobs may still be created if the user cancels/aborts.
 * There is no deleteAutomation mutation available for rollback.
 *
 * @param params - Parameters for template/automation creation
 * @returns Result containing template and automation IDs
 */
export async function createTemplateAndAutomation(params: {
  jobConfig: JobConfig;
  processedTasks: TaskInput[];
  selectedTemplate: JobTemplateFieldsFragment | null;
  isTemplateModified: (template: JobTemplateFieldsFragment) => boolean;
  createJobTemplate: (input: {
    input: JobTemplateInput;
  }) => MutationResult<CreateJobTemplateMutation>;
  createAutomation: (input: {
    input: AutomationInput;
  }) => MutationResult<CreateAutomationMutation>;
  formData: {
    name: string;
    notes?: RawDraftContentState | null;
    tags?: string[] | null;
    includeAttributes?: string[] | null;
    jobAttributes?: string[] | null;
    attachments?: Array<{
      id: string;
      kind: Kind;
      name?: string;
      preview: string;
    }>;
    guestyCleaningStatus?: boolean;
    repeat: {
      numOfRepeats: string;
      value: RepeatValue;
    };
    scopeIds: string[];
    owner?: string[] | null;
    assignee?: string[] | null;
    scheduledStartTime?: string | null;
    scheduledStartDate?: string | null;
    timeZone: string;
  };
}): Promise<TemplateAutomationResult> {
  const {
    jobConfig,
    processedTasks,
    selectedTemplate,
    isTemplateModified,
    createJobTemplate,
    createAutomation,
    formData,
  } = params;

  let templateId: string | null = null;
  let automationId: string | null = null;
  let createdTemplate = false;
  let createdAutomation = false;

  try {
    // Step 1: Ensure template exists
    const templateResult = await ensureTemplateForAutomation(
      jobConfig,
      processedTasks,
      selectedTemplate,
      isTemplateModified,
      createJobTemplate,
      formData
    );

    templateId = templateResult.templateId;
    createdTemplate = templateResult.createdTemplate;

    // Step 2: Create automation if template exists
    if (templateId) {
      automationId = await createRepeatAutomation(
        templateId,
        {
          name: formData.name,
          numOfRepeats: formData.repeat.numOfRepeats,
          scopeIds: formData.scopeIds,
          owner: formData.owner,
          assignee: formData.assignee,
          scheduledStartTime: formData.scheduledStartTime,
          scheduledStartDate: formData.scheduledStartDate,
          timeZone: formData.timeZone,
          repeatValue: formData.repeat.value,
        },
        createAutomation
      );
      createdAutomation = true;
    }

    return {
      templateId,
      automationId,
      createdTemplate,
      createdAutomation,
    };
  } catch (error) {
    // Re-throw with proper error handling
    if (error instanceof TemplateAutomationError) {
      throw error;
    }

    // Wrap unexpected errors
    throw new TemplateAutomationError(
      'An unexpected error occurred while creating template/automation. Please try again.',
      'unknown',
      error
    );
  }
}
